import React, { useState, useMemo } from "react";
import { TablePageHeader } from "./TablePageHeader";
import { UnifiedTableToolbar } from "./UnifiedTableToolbar";
import { ReusableTable } from "../ReusableTable";
import { Loader2 } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";

interface UnifiedTableLayoutProps {
  title: string;
  data: any[];
  columns: any[];
  searchFields?: string[];
  isLoading?: boolean;
  onAddNew?: () => void;
  addButtonText?: string;
  onImport?: () => void;
  onExport?: () => void;
  onExportTemplate?: () => void;
  onRefresh?: () => void;
  rowActions?: any[];
  onRowClick?: (row: any) => void;
  sortBy?: { key: string; direction: "asc" | "desc" | null };
  onSortChange?: (key: string) => void;
  additionalHeaderActions?: React.ReactNode;
}

export const UnifiedTableLayout: React.FC<UnifiedTableLayoutProps> = ({
  title,
  data,
  columns,
  searchFields = [],
  isLoading = false,
  onAddNew,
  addButtonText,
  onImport,
  onExport,
  onExportTemplate,
  onRefresh,
  rowActions,
  onRowClick,
  sortBy,
  onSortChange,
  additionalHeaderActions
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columns.filter(col => !col.hidden).map(col => col.key)
  );
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!debouncedSearchQuery) return data;
    
    return data.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        return value && value.toString().toLowerCase().includes(debouncedSearchQuery.toLowerCase());
      })
    );
  }, [data, debouncedSearchQuery, searchFields]);

  // Get visible column configurations
  const visibleColumnConfigs = useMemo(() => {
    return columns.filter(col => visibleColumns.includes(col.key));
  }, [columns, visibleColumns]);

  const handleColumnFilterChange = (key: string, value: any) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="table-page-container">
      <div className="table-page-content">
        <TablePageHeader
          title={title}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onAddNew={onAddNew}
          addButtonText={addButtonText}
          columns={columns}
          visibleColumns={visibleColumns}
          onColumnVisibilityChange={setVisibleColumns}
          onRefresh={onRefresh}
          onImport={onImport}
          onExport={onExport}
          onExportTemplate={onExportTemplate}
        >
          {additionalHeaderActions}
        </TablePageHeader>

        <div className="table-container">

          <div className="relative">
            {isLoading && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                <div className="flex flex-col items-center gap-2 bg-background/90 p-4 rounded-md shadow">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading data...</p>
                </div>
              </div>
            )}
            
            <ReusableTable
              data={filteredData}
              columns={visibleColumnConfigs}
              rowActions={rowActions}
              sortBy={sortBy}
              onSortChange={onSortChange}
              onRowClick={onRowClick}
              onColumnFilterChange={handleColumnFilterChange}
              columnFilters={columnFilters}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};